import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import '../styles/ControleCustos.css';
import { apiService } from '../services/ApiService';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { gerarRelatorioExcel, gerarRelatorioEntidade } from '../utils/ExcelReport';
import { gerarRelatorioFinanceiroHTML, salvarRelatorioHTML } from '../utils/RelatorioFinanceiroHTML';

const ControleCustos = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [transacoes, setTransacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    tipo: 'entrada',
    descricao: '',
    valor: '',
    categoria: 'geral',
    data: new Date().toISOString().substring(0, 10),
    formaPagamento: 'dinheiro',
    entidadeNome: '',
    entidadeTipo: 'cliente',
    reembolsadoPeloCliente: false,
    isParcelamento: false,
    valorTotal: '',
    percentualEntrada: '',
    numeroParcelas: 1,
    intervaloParcelas: 30,
    obraId: '',
    dataRecebimento: new Date().toISOString().substring(0, 10),
    primeiraParcelaMesmoDia: false
  });
  const [filtros, setFiltros] = useState({
    tipo: 'todos',
    dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().substring(0, 10),
    dataFim: new Date().toISOString().substring(0, 10),
    categoria: 'todos',
    entidadeNome: 'todos',
    entidadeTipo: 'todos'
  });
  const [editandoId, setEditandoId] = useState(null);
  const [saldoAtual, setSaldoAtual] = useState(0);
  const [entidadesDisponiveis, setEntidadesDisponiveis] = useState([]);
  const [resumoPorEntidade, setResumoPorEntidade] = useState({});
  const [modalAberta, setModalAberta] = useState(false);
  const [modalData, setModalData] = useState({
    tipo: 'entrada',
    descricao: '',
    valor: '',
    categoria: 'geral',
    data: new Date().toISOString().substring(0, 10),
    formaPagamento: 'dinheiro',
    entidadeNome: '',
    entidadeTipo: 'cliente',
    reembolsadoPeloCliente: false
  });
  const [confirmacaoReembolso, setConfirmacaoReembolso] = useState(false);
  const [transacaoParaReembolso, setTransacaoParaReembolso] = useState(null);

  // Função para gerar ID aleatório para transações
  const gerarIdTransacao = () => {
    // Gerar timestamp atual em milissegundos
    const timestamp = new Date().getTime();
    
    // Gerar 4 dígitos aleatórios
    const randomPart = Math.floor(1000 + Math.random() * 9000);
    
    // Combinar timestamp com parte aleatória
    const idNumerico = `${timestamp}${randomPart}`;
    
    // Retornar apenas o número, sem o prefixo
    return idNumerico;
  };

  // Lista de categorias disponíveis
  const categorias = {
    geral: 'Geral',
    salario: 'Salário de Funcionário',
    terceiros: 'Pagamento de Terceiros',
    diaria: 'Diária',
    material: 'Material de Construção',
    aluguel: 'Aluguel de Equipamentos',
    ferramentas: 'Ferramentas',
    transporte: 'Transporte',
    administrativo: 'Administrativo',
    impostos: 'Impostos e Taxas',
    reembolso: 'Reembolso',
    pagamento_cliente: 'Pagamento de Cliente',
    pagamento_obra: 'Pagamento de Obra',
    outros: 'Outros'
  };

  // Tipos de entidade
  const tiposEntidade = {
    cliente: 'Cliente',
    funcionario: 'Funcionário',
    outros: 'Outros'
  };

  // Formas de pagamento
  const formasPagamento = {
    dinheiro: 'Dinheiro',
    cartao: 'Cartão de Crédito',
    pix: 'PIX',
    transferencia: 'Transferência',
    cheque: 'Cheque',
    boleto: 'Boleto'
  };

  // Extrair entidades únicas das transações
  const extrairEntidadesUnicas = (transacoes) => {
    if (!Array.isArray(transacoes) || transacoes.length === 0) return [];
    
    // Extrair todos os nomes das transações
    const entidades = transacoes
      .map(t => t.entidadeNome)
      .filter(e => e && e.trim() !== '')
      .reduce((unique, item) => {
        return unique.includes(item) ? unique : [...unique, item];
      }, []);
    
    return entidades.sort();
  };

  // Calcular resumo financeiro por entidade
  const calcularResumoPorEntidade = (transacoes) => {
    if (!Array.isArray(transacoes) || transacoes.length === 0) return {};
    
    const resumo = {};
    
    // Agrupar transações por entidade e calcular totais
    transacoes.forEach(t => {
      if (!t.entidadeNome || t.entidadeNome.trim() === '') return;
      
      const valor = parseFloat(t.valor) || 0;
      const entidadeKey = t.entidadeNome;
      
      if (!resumo[entidadeKey]) {
        resumo[entidadeKey] = {
          nome: t.entidadeNome,
          tipo: t.entidadeTipo || 'outros',
          entradas: 0,
          saidas: 0,
          saldo: 0
        };
      }
      
      if (t.tipo === 'entrada') {
        resumo[entidadeKey].entradas += valor;
      } else {
        resumo[entidadeKey].saidas += valor;
      }
      
      resumo[entidadeKey].saldo = resumo[entidadeKey].entradas - resumo[entidadeKey].saidas;
    });
    
    return resumo;
  };

  // Buscar transações do usuário
  useEffect(() => {
    const buscarTransacoes = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Usar o método do ApiService para obter todas as transações
        // Passamos apenas os filtros de data, tipo e categoria que a API suporta
        const apiFilterParams = {
          tipo: filtros.tipo,
          categoria: filtros.categoria,
          dataInicio: filtros.dataInicio,
          dataFim: filtros.dataFim
        };
        
        const response = await apiService.getCustosFiltrados(apiFilterParams);
        
        if (response.success) {
          // Ajuste no acesso aos dados - verificar várias possíveis estruturas
          let transacoesData = [];
          
          // Verificar diferentes possíveis estruturas na resposta
          if (response.data.transacoes) {
            transacoesData = response.data.transacoes;
          } else if (response.data.data && response.data.data.transacoes) {
            transacoesData = response.data.data.transacoes;
          } else if (Array.isArray(response.data)) {
            transacoesData = response.data;
          }
          
          // Aplicar filtros adicionais no lado do cliente para entidadeNome e entidadeTipo
          if (filtros.entidadeNome !== 'todos') {
            transacoesData = transacoesData.filter(t => 
              t.entidadeNome && t.entidadeNome === filtros.entidadeNome
            );
          }
          
          if (filtros.entidadeTipo !== 'todos') {
            transacoesData = transacoesData.filter(t => 
              t.entidadeTipo && t.entidadeTipo === filtros.entidadeTipo
            );
          }
          
          
          setTransacoes(transacoesData);
          
          // Extrair entidades únicas de TODAS as transações para o dropdown de filtro
          // (não das transações já filtradas)
          let todasTransacoes = [];
          if (response.data.transacoes) {
            todasTransacoes = response.data.transacoes;
          } else if (response.data.data && response.data.data.transacoes) {
            todasTransacoes = response.data.data.transacoes;
          } else if (Array.isArray(response.data)) {
            todasTransacoes = response.data;
          }
          
          const entidades = extrairEntidadesUnicas(todasTransacoes);
          setEntidadesDisponiveis(entidades);
          
          // Calcular resumo por entidade apenas para as transações filtradas
          const resumo = calcularResumoPorEntidade(transacoesData);
          setResumoPorEntidade(resumo);
          
          // Obter saldo da API - ajustar acesso também
          let saldo = 0;
          if (typeof response.data.saldo !== 'undefined') {
            saldo = parseFloat(response.data.saldo || 0);
          } else if (response.data.data && typeof response.data.data.saldo !== 'undefined') {
            saldo = parseFloat(response.data.data.saldo || 0);
          }
          
          // Se estamos filtrando por entidade, recalcular o saldo com base nas transações filtradas
          if (filtros.entidadeNome !== 'todos' || filtros.entidadeTipo !== 'todos') {
            saldo = transacoesData.reduce((acc, t) => {
              const valor = parseFloat(t.valor) || 0;
              return t.tipo === 'entrada' ? acc + valor : acc - valor;
            }, 0);
          }
          
          setSaldoAtual(saldo);
        } else {
          toast.error("Erro ao carregar transações: " + response.error);
        }
      } catch (error) {
        console.error("Erro ao buscar transações:", error);
        toast.error("Erro ao carregar transações. Tente novamente.");
      } finally {
        setLoading(false);
      }
    };
    
    buscarTransacoes();
  }, [currentUser, filtros]);

  // Filtrar transações já é feito na API, mas mantemos isso para compatibilidade
  const transacoesFiltradas = transacoes;

  // Calcular saldo do período filtrado (também vem da API, mas mantemos para compatibilidade)
  const saldoFiltrado = saldoAtual;

  // Gerenciar mudanças no formulário
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Se estiver alterando o valor inicial e for obra parcelada, atualizar o valor total
    if (name === 'valor' && formData.categoria === 'pagamento_obra' && formData.isParcelamento) {
      setFormData(prev => ({
        ...prev,
        valor: value.replace(/[^0-9.]/g, ''),
        valorTotal: value.replace(/[^0-9.]/g, '') // Auto-preencher valor total
      }));
      return;
    }
    
    // Se estiver alterando a data inicial e for obra parcelada, atualizar a data de recebimento
    if (name === 'data' && formData.categoria === 'pagamento_obra' && formData.isParcelamento) {
      setFormData(prev => ({
        ...prev,
        data: value,
        dataRecebimento: value // Auto-preencher data de recebimento
      }));
      return;
    }
    
    // Se estiver marcando como obra parcelada, copiar os valores
    if (name === 'isParcelamento' && checked && formData.categoria === 'pagamento_obra') {
      setFormData(prev => ({
        ...prev,
        isParcelamento: checked,
        valorTotal: prev.valor, // Copiar valor inicial para valor total
        dataRecebimento: prev.data // Copiar data inicial para data de recebimento
      }));
      return;
    }
    
    // Para todas as outras mudanças, comportamento normal
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'valor' ? value.replace(/[^0-9.]/g, '') : value)
    }));
  };

  // Função para verificar e registrar parcelas automaticamente
  const verificarParcelasPendentes = async () => {
    try {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      // Buscar todas as transações de obra parcelada
      const response = await apiService.getCustosFiltrados({
        categoria: 'pagamento_obra'
      });

      if (!response.success) return;

      let transacoesData = [];
      if (response.data.transacoes) {
        transacoesData = response.data.transacoes;
      } else if (response.data.data && response.data.data.transacoes) {
        transacoesData = response.data.data.transacoes;
      } else if (Array.isArray(response.data)) {
        transacoesData = response.data;
      }

      // Agrupar transações por obraId
      const obras = {};
      transacoesData.forEach(transacao => {
        if (transacao.obraId && transacao.parcelaInfo) {
          if (!obras[transacao.obraId]) {
            obras[transacao.obraId] = [];
          }
          obras[transacao.obraId].push(transacao);
        }
      });

      // Verificar cada obra
      for (const [obraId, parcelas] of Object.entries(obras)) {
        // Ordenar parcelas por número
        parcelas.sort((a, b) => a.parcelaInfo.numero - b.parcelaInfo.numero);
        
        // Encontrar a última parcela registrada
        const ultimaParcela = parcelas[parcelas.length - 1];
        
        // Se ainda há parcelas pendentes
        if (ultimaParcela.parcelaInfo.numero < ultimaParcela.parcelaInfo.total - 1) {
          const dataUltimaParcela = new Date(ultimaParcela.data);
          const proximaData = new Date(dataUltimaParcela);
          proximaData.setDate(proximaData.getDate() + ultimaParcela.parcelaInfo.intervaloParcelas);
          
          // Se a próxima data de parcela chegou
          if (proximaData <= hoje) {
            const novaParcela = {
              id: gerarIdTransacao(),
              tipo: 'entrada',
              descricao: `Parcela ${ultimaParcela.parcelaInfo.numero + 1}/${ultimaParcela.parcelaInfo.total - 1} - ${ultimaParcela.descricao.split(' - ')[1]}`,
              valor: ultimaParcela.parcelaInfo.valorParcela,
              categoria: 'pagamento_obra',
              data: proximaData.toISOString().split('T')[0],
              formaPagamento: ultimaParcela.formaPagamento,
              entidadeNome: ultimaParcela.entidadeNome,
              entidadeTipo: ultimaParcela.entidadeTipo,
              obraId: obraId,
              parcelaInfo: {
                ...ultimaParcela.parcelaInfo,
                numero: ultimaParcela.parcelaInfo.numero + 1,
                dataRecebimento: proximaData.toISOString().split('T')[0]
              }
            };

            const response = await apiService.createCusto(novaParcela);
            if (response.success) {
              toast.success(`Parcela ${novaParcela.parcelaInfo.numero} registrada automaticamente!`);
            }
          }
        }
      }
    } catch (error) {
      console.error("Erro ao verificar parcelas pendentes:", error);
    }
  };

  // Adicionar verificação periódica de parcelas
  useEffect(() => {
    // Verificar parcelas ao carregar a página
    verificarParcelasPendentes();

    // Verificar parcelas a cada hora
    const intervalo = setInterval(verificarParcelasPendentes, 3600000);

    return () => clearInterval(intervalo);
  }, []);

  // Modificar a função gerarParcelasObra
  const gerarParcelasObra = async (dadosObra) => {
    try {
      const valorTotal = parseFloat(dadosObra.valorTotal);
      const percentualEntrada = parseFloat(dadosObra.percentualEntrada) / 100;
      const numeroParcelas = parseInt(dadosObra.numeroParcelas);
      const valorEntrada = valorTotal * percentualEntrada;
      const valorParcela = (valorTotal - valorEntrada) / numeroParcelas;
      const obraId = gerarIdTransacao();
      
      const transacoes = [];
      const dataEntrada = new Date(dadosObra.data);
      const dataRecebimento = new Date(dadosObra.dataRecebimento);
      
      // Criar transação da entrada
      const entradaId = gerarIdTransacao();
      const transacaoEntrada = {
        id: entradaId,
        tipo: 'entrada',
        descricao: `Entrada - ${dadosObra.descricao}`,
        valor: valorEntrada.toFixed(2),
        categoria: 'pagamento_obra',
        data: dataEntrada.toISOString().split('T')[0],
        formaPagamento: dadosObra.formaPagamento,
        entidadeNome: dadosObra.entidadeNome,
        entidadeTipo: dadosObra.entidadeTipo,
        obraId: obraId,
        parcelaInfo: {
          numero: 0,
          total: numeroParcelas + 1,
          valorTotal: valorTotal,
          valorEntrada: valorEntrada,
          valorParcela: valorParcela,
          intervaloParcelas: dadosObra.intervaloParcelas,
          dataRecebimento: dataRecebimento.toISOString().split('T')[0]
        }
      };
      
      transacoes.push(transacaoEntrada);
      
      // Criar transações das parcelas
      for (let i = 1; i <= numeroParcelas; i++) {
        let dataParcela;
        
        if (i === 1) {
          if (dadosObra.primeiraParcelaMesmoDia) {
            // Se for a primeira parcela e o usuário escolheu mesmo dia, usar a data da entrada
            dataParcela = new Date(dataEntrada);
          } else {
            // Se não escolheu mesmo dia, calcular a partir da data de recebimento + intervalo
            dataParcela = new Date(dataRecebimento);
            dataParcela.setDate(dataParcela.getDate() + dadosObra.intervaloParcelas);
          }
        } else {
          // Para as demais parcelas, calcular a partir da data de recebimento + (intervalo * número da parcela)
          dataParcela = new Date(dataRecebimento);
          dataParcela.setDate(dataParcela.getDate() + (i * dadosObra.intervaloParcelas));
        }
        
        const parcelaId = gerarIdTransacao();
        const transacaoParcela = {
          id: parcelaId,
          tipo: 'entrada',
          descricao: `Parcela ${i}/${numeroParcelas} - ${dadosObra.descricao}`,
          valor: valorParcela.toFixed(2),
          categoria: 'pagamento_obra',
          data: dataParcela.toISOString().split('T')[0],
          formaPagamento: dadosObra.formaPagamento,
          entidadeNome: dadosObra.entidadeNome,
          entidadeTipo: dadosObra.entidadeTipo,
          obraId: obraId,
          parcelaInfo: {
            numero: i,
            total: numeroParcelas + 1,
            valorTotal: valorTotal,
            valorEntrada: valorEntrada,
            valorParcela: valorParcela,
            intervaloParcelas: dadosObra.intervaloParcelas,
            dataRecebimento: dataParcela.toISOString().split('T')[0]
          }
        };
        
        transacoes.push(transacaoParcela);
      }
      
      // Salvar todas as transações
      for (const transacao of transacoes) {
        const response = await apiService.createCusto(transacao);
        if (!response.success) {
          throw new Error(`Erro ao criar parcela: ${response.error}`);
        }
      }
      
      toast.success(`Obra parcelada com sucesso! ${numeroParcelas + 1} transações criadas.`);
      return true;
    } catch (error) {
      console.error("Erro ao gerar parcelas:", error);
      toast.error("Erro ao criar parcelas: " + error.message);
      return false;
    }
  };

  // Modificar handleSubmit para incluir a lógica de parcelamento
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.descricao || !formData.valor || parseFloat(formData.valor) <= 0) {
      toast.error("Preencha todos os campos corretamente.");
      return;
    }
    
    try {
      // Se for um parcelamento de obra
      if (formData.isParcelamento && formData.categoria === 'pagamento_obra') {
        if (!formData.valorTotal || !formData.percentualEntrada || !formData.numeroParcelas) {
          toast.error("Preencha todos os campos do parcelamento.");
          return;
        }
        
        const sucesso = await gerarParcelasObra(formData);
        if (sucesso) {
          // Resetar formulário
          setFormData({
            tipo: 'entrada',
            descricao: '',
            valor: '',
            categoria: 'geral',
            data: new Date().toISOString().substring(0, 10),
            formaPagamento: 'dinheiro',
            entidadeNome: '',
            entidadeTipo: 'cliente',
            reembolsadoPeloCliente: false,
            isParcelamento: false,
            valorTotal: '',
            percentualEntrada: '',
            numeroParcelas: 1,
            intervaloParcelas: 30,
            obraId: '',
            dataRecebimento: new Date().toISOString().substring(0, 10),
            primeiraParcelaMesmoDia: false
          });
          
          // Recarregar transações
          recarregarTransacoes();
        }
        return;
      }
      
      // Lógica existente para transações normais
      const dataAjustada = {
        ...formData,
        data: new Date(formData.data + 'T12:00:00').toISOString().split('T')[0]
      };
      
      if (editandoId) {
        // Atualizar transação existente
        const response = await apiService.updateCusto(editandoId, dataAjustada);
        
        if (response.success) {
          toast.success("Transação atualizada com sucesso!");
          setEditandoId(null);
        } else {
          toast.error("Erro ao atualizar: " + response.error);
        }
      } else {
        // Adicionar nova transação com ID gerado
        const transacaoId = gerarIdTransacao();
        const response = await apiService.createCusto({
          ...dataAjustada,
          id: transacaoId
        });
        
        if (response.success) {
          toast.success(`${formData.tipo === 'entrada' ? 'Receita' : 'Despesa'} registrada com sucesso!`);
        } else {
          toast.error("Erro ao salvar: " + response.error);
        }
      }
      
      // Resetar formulário
      setFormData({
        tipo: 'entrada',
        descricao: '',
        valor: '',
        categoria: 'geral',
        data: new Date().toISOString().substring(0, 10),
        formaPagamento: 'dinheiro',
        entidadeNome: '',
        entidadeTipo: 'cliente',
        reembolsadoPeloCliente: false,
        isParcelamento: false,
        valorTotal: '',
        percentualEntrada: '',
        numeroParcelas: 1,
        intervaloParcelas: 30,
        obraId: '',
        dataRecebimento: new Date().toISOString().substring(0, 10),
        primeiraParcelaMesmoDia: false
      });
      
      // Recarregar transações
      recarregarTransacoes();
    } catch (error) {
      console.error("Erro ao salvar transação:", error);
      toast.error("Erro ao salvar. Tente novamente.");
    }
  };

  // Editar transação
  const handleEditar = (transacao) => {
    setModalData({
      tipo: transacao.tipo,
      descricao: transacao.descricao,
      valor: transacao.valor.toString(),
      categoria: transacao.categoria || 'geral',
      data: new Date(transacao.data).toISOString().substring(0, 10),
      formaPagamento: transacao.formaPagamento || 'dinheiro',
      entidadeNome: transacao.entidadeNome || '',
      entidadeTipo: transacao.entidadeTipo || 'cliente',
      reembolsadoPeloCliente: transacao.reembolsadoPeloCliente || false
    });
    
    setEditandoId(transacao.id);
    setModalAberta(true);
  };

  // Excluir transação
  const handleExcluir = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir esta transação? Esta ação não pode ser desfeita.")) {
      try {
        // Excluir transação usando ApiService
        const response = await apiService.deleteCusto(id);
        
        if (response.success) {
          // Atualizar lista de transações
          setTransacoes(prev => prev.filter(t => t.id !== id));
          
          // Recarregar saldo e dados atualizados
          const apiFilterParams = {
            tipo: filtros.tipo,
            categoria: filtros.categoria,
            dataInicio: filtros.dataInicio,
            dataFim: filtros.dataFim
          };
          
          const reloadResponse = await apiService.getCustosFiltrados(apiFilterParams);
          
          if (reloadResponse.success) {
            // Ajuste no acesso aos dados - verificar várias possíveis estruturas
            let transacoesData = [];
            
            // Verificar diferentes possíveis estruturas na resposta
            if (reloadResponse.data.transacoes) {
              transacoesData = reloadResponse.data.transacoes;
            } else if (reloadResponse.data.data && reloadResponse.data.data.transacoes) {
              transacoesData = reloadResponse.data.data.transacoes;
            } else if (Array.isArray(reloadResponse.data)) {
              transacoesData = reloadResponse.data;
            }
            
            // Aplicar filtros adicionais no lado do cliente para entidadeNome e entidadeTipo
            if (filtros.entidadeNome !== 'todos') {
              transacoesData = transacoesData.filter(t => 
                t.entidadeNome && t.entidadeNome === filtros.entidadeNome
              );
            }
            
            if (filtros.entidadeTipo !== 'todos') {
              transacoesData = transacoesData.filter(t => 
                t.entidadeTipo && t.entidadeTipo === filtros.entidadeTipo
              );
            }
            
            setTransacoes(transacoesData);
            
            // Extrair entidades únicas de TODAS as transações
            let todasTransacoes = [];
            if (reloadResponse.data.transacoes) {
              todasTransacoes = reloadResponse.data.transacoes;
            } else if (reloadResponse.data.data && reloadResponse.data.data.transacoes) {
              todasTransacoes = reloadResponse.data.data.transacoes;
            } else if (Array.isArray(reloadResponse.data)) {
              todasTransacoes = reloadResponse.data;
            }
            
            const entidades = extrairEntidadesUnicas(todasTransacoes);
            setEntidadesDisponiveis(entidades);
            
            // Calcular resumo por entidade apenas para as transações filtradas
            const resumo = calcularResumoPorEntidade(transacoesData);
            setResumoPorEntidade(resumo);
            
            // Obter saldo da API - ajustar acesso também
            let saldo = 0;
            if (typeof reloadResponse.data.saldo !== 'undefined') {
              saldo = parseFloat(reloadResponse.data.saldo || 0);
            } else if (reloadResponse.data.data && typeof reloadResponse.data.data.saldo !== 'undefined') {
              saldo = parseFloat(reloadResponse.data.data.saldo || 0);
            }
            
            // Se estamos filtrando por entidade, recalcular o saldo com base nas transações filtradas
            if (filtros.entidadeNome !== 'todos' || filtros.entidadeTipo !== 'todos') {
              saldo = transacoesData.reduce((acc, t) => {
                const valor = parseFloat(t.valor) || 0;
                return t.tipo === 'entrada' ? acc + valor : acc - valor;
              }, 0);
            }
            
            setSaldoAtual(saldo);
          }
          
          toast.success("Transação excluída com sucesso!");
        } else {
          toast.error("Erro ao excluir: " + response.error);
        }
      } catch (error) {
        console.error("Erro ao excluir transação:", error);
        toast.error("Erro ao excluir. Tente novamente.");
      }
    }
  };

  // Cancelar edição
  const handleCancelarEdicao = () => {
    setEditandoId(null);
    setModalAberta(false);
  };

  // Handle modal form change
  const handleModalChange = (e) => {
    const { name, value, type, checked } = e.target;
    setModalData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox' ? checked :
        name === 'valor' ? value.replace(/[^0-9.]/g, '') :
        name === 'tipo' ? value :
        value
    }));
  };

  // Handle modal submit
  const handleModalSubmit = async (e) => {
    e.preventDefault();
    
    if (!modalData.descricao || !modalData.valor || parseFloat(modalData.valor) <= 0) {
      toast.error("Preencha todos os campos corretamente.");
      return;
    }
    
    try {
      // Verificar se estamos adicionando um reembolso
      const reembolsoAdicionado = 
        modalData.tipo === 'saida' && 
        modalData.reembolsadoPeloCliente && 
        (!editandoId || !transacoes.find(t => t.id === editandoId)?.reembolsadoPeloCliente);
      
      // Atualizar a transação existente
      const response = await apiService.updateCusto(editandoId, modalData);
      
      if (response.success) {
        toast.success("Transação atualizada com sucesso!");
        
        // Se foi marcado como reembolsado, criar uma transação de entrada equivalente
        if (reembolsoAdicionado) {
          const reembolsoId = gerarIdTransacao();
          const reembolsoData = {
            id: reembolsoId,
            tipo: 'entrada',
            descricao: `Reembolso: ${modalData.descricao}`,
            valor: modalData.valor,
            categoria: modalData.categoria,
            data: modalData.data,
            formaPagamento: modalData.formaPagamento,
            entidadeNome: modalData.entidadeNome,
            entidadeTipo: modalData.entidadeTipo
          };
          
          const reembolsoResponse = await apiService.createCusto(reembolsoData);
          
          if (reembolsoResponse.success) {
            toast.success("Reembolso registrado automaticamente como entrada!");
          } else {
            toast.error("Erro ao registrar reembolso: " + reembolsoResponse.error);
          }
        }
        
        // Fechar modal e redefinir o estado
        setEditandoId(null);
        setModalAberta(false);
        
        // Recarregar transações utilizando os mesmos filtros atuais
        recarregarTransacoes();
      } else {
        toast.error("Erro ao atualizar: " + response.error);
      }
    } catch (error) {
      console.error("Erro ao salvar transação:", error);
      toast.error("Erro ao salvar. Tente novamente.");
    }
  };
  
  // Função para recarregar transações
  const recarregarTransacoes = async () => {
    try {
      // Usar os filtros atuais
      const apiFilterParams = {
        tipo: filtros.tipo,
        categoria: filtros.categoria,
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim
      };
      
      const reloadResponse = await apiService.getCustosFiltrados(apiFilterParams);
      
      if (reloadResponse.success) {
        // Ajuste no acesso aos dados - verificar várias possíveis estruturas
        let transacoesData = [];
        
        // Verificar diferentes possíveis estruturas na resposta
        if (reloadResponse.data.transacoes) {
          transacoesData = reloadResponse.data.transacoes;
        } else if (reloadResponse.data.data && reloadResponse.data.data.transacoes) {
          transacoesData = reloadResponse.data.data.transacoes;
        } else if (Array.isArray(reloadResponse.data)) {
          transacoesData = reloadResponse.data;
        }
        
        // Aplicar filtros adicionais no lado do cliente para entidadeNome e entidadeTipo
        if (filtros.entidadeNome !== 'todos') {
          transacoesData = transacoesData.filter(t => 
            t.entidadeNome && t.entidadeNome === filtros.entidadeNome
          );
        }
        
        if (filtros.entidadeTipo !== 'todos') {
          transacoesData = transacoesData.filter(t => 
            t.entidadeTipo && t.entidadeTipo === filtros.entidadeTipo
          );
        }
        
        setTransacoes(transacoesData);
        
        // Extrair entidades únicas de TODAS as transações
        let todasTransacoes = [];
        if (reloadResponse.data.transacoes) {
          todasTransacoes = reloadResponse.data.transacoes;
        } else if (reloadResponse.data.data && reloadResponse.data.data.transacoes) {
          todasTransacoes = reloadResponse.data.data.transacoes;
        } else if (Array.isArray(reloadResponse.data)) {
          todasTransacoes = reloadResponse.data;
        }
        
        const entidades = extrairEntidadesUnicas(todasTransacoes);
        setEntidadesDisponiveis(entidades);
        
        // Calcular resumo por entidade apenas para as transações filtradas
        const resumo = calcularResumoPorEntidade(transacoesData);
        setResumoPorEntidade(resumo);
        
        // Obter saldo da API - ajustar acesso também
        let saldo = 0;
        if (typeof reloadResponse.data.saldo !== 'undefined') {
          saldo = parseFloat(reloadResponse.data.saldo || 0);
        } else if (reloadResponse.data.data && typeof reloadResponse.data.data.saldo !== 'undefined') {
          saldo = parseFloat(reloadResponse.data.data.saldo || 0);
        }
        
        // Se estamos filtrando por entidade, recalcular o saldo com base nas transações filtradas
        if (filtros.entidadeNome !== 'todos' || filtros.entidadeTipo !== 'todos') {
          saldo = transacoesData.reduce((acc, t) => {
            const valor = parseFloat(t.valor) || 0;
            return t.tipo === 'entrada' ? acc + valor : acc - valor;
          }, 0);
        }
        
        setSaldoAtual(saldo);
      }
    } catch (error) {
      console.error("Erro ao recarregar transações:", error);
    }
  };

  // Atualizar filtros
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Adicionar função para definir filtro de período
  const definirFiltroPeriodo = (tipo) => {
    const hoje = new Date();
    let dataInicio, dataFim;
    
    switch (tipo) {
      case 'inicio_ano':
        dataInicio = new Date(hoje.getFullYear(), 0, 1); // 1º de janeiro
        dataFim = hoje;
        break;
      case 'todo_periodo':
        dataInicio = new Date(2000, 0, 1); // Data inicial arbitrária
        dataFim = hoje;
        break;
      default:
        return;
    }
    
    setFiltros(prev => ({
      ...prev,
      dataInicio: dataInicio.toISOString().substring(0, 10),
      dataFim: dataFim.toISOString().substring(0, 10)
    }));
    
    // Recarregar transações com os novos filtros
    recarregarTransacoes();
  };

  // Handlers de navegação para o Header
  const handleNavigateToHome = () => {
    navigate('/home');
  };

  const handleNavigateToOrcamentos = () => {
    navigate('/orcamentos');
  };

  const handleNavigateToDiarioObra = () => {
    navigate('/diario-obra');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Erro ao fazer logout. Tente novamente.');
    }
  };

  // Modificar handleMarcarReembolso
  const handleMarcarReembolso = (transacao) => {
    setTransacaoParaReembolso(transacao);
    setConfirmacaoReembolso(true);
  };
  
  // Função para confirmar o reembolso
  const confirmarReembolso = async () => {
    try {
      if (!transacaoParaReembolso) return;
      
      // Preparar dados atualizados com reembolsadoPeloCliente = true
      const transacaoAtualizada = {
        ...transacaoParaReembolso,
        reembolsadoPeloCliente: true
      };
      
      // Atualizar a transação
      const response = await apiService.updateCusto(transacaoParaReembolso.id, transacaoAtualizada);
      
      if (response.success) {
        toast.success("Transação marcada como reembolsada!");
        
        // Criar transação de entrada equivalente (reembolso) com ID gerado
        const reembolsoId = gerarIdTransacao();
        const reembolsoData = {
          id: reembolsoId,
          tipo: 'entrada',
          descricao: `Reembolso: ${transacaoParaReembolso.descricao}`,
          valor: transacaoParaReembolso.valor,
          categoria: transacaoParaReembolso.categoria,
          data: transacaoParaReembolso.data,
          formaPagamento: transacaoParaReembolso.formaPagamento,
          entidadeNome: transacaoParaReembolso.entidadeNome,
          entidadeTipo: transacaoParaReembolso.entidadeTipo
        };
        
        const reembolsoResponse = await apiService.createCusto(reembolsoData);
        
        if (reembolsoResponse.success) {
          toast.success("Reembolso registrado automaticamente como entrada!");
        } else {
          toast.error("Erro ao registrar reembolso: " + reembolsoResponse.error);
        }
        
        // Recarregar transações
        recarregarTransacoes();
      } else {
        toast.error("Erro ao marcar como reembolsado: " + response.error);
      }
    } catch (error) {
      console.error("Erro ao marcar como reembolsado:", error);
      toast.error("Erro ao processar. Tente novamente.");
    } finally {
      setConfirmacaoReembolso(false);
      setTransacaoParaReembolso(null);
    }
  };
  
  // Função para cancelar o reembolso
  const cancelarReembolso = () => {
    setConfirmacaoReembolso(false);
    setTransacaoParaReembolso(null);
  };

  // Função para gerar relatório Excel (sem cor)
  const handleGerarRelatorioExcel = () => {
    try {
      const dados = {
        transacoes: transacoesFiltradas,
        saldoAtual,
        saldoFiltrado,
        resumoPorEntidade,
        filtros
      };
      const nomeArquivo = gerarRelatorioExcel(dados);
      toast.success(`Relatório Excel gerado com sucesso: ${nomeArquivo}`);
    } catch (error) {
      console.error('Erro ao gerar relatório Excel:', error);
      toast.error('Erro ao gerar relatório. Tente novamente.');
    }
  };

  // Função para gerar relatório HTML estilizado
  const handleGerarRelatorioHTML = () => {
    try {
      const dados = {
        transacoes: transacoesFiltradas,
        saldoAtual,
        saldoFiltrado,
        resumoPorEntidade,
        filtros
      };
      const html = gerarRelatorioFinanceiroHTML(dados);
      salvarRelatorioHTML('relatorio_financeiro.html', html);
      toast.success('Relatório HTML gerado com sucesso! Abra no Excel para visualizar.');
    } catch (error) {
      console.error('Erro ao gerar relatório HTML:', error);
      toast.error('Erro ao gerar relatório. Tente novamente.');
    }
  };

  // Função para formatar data no padrão brasileiro
  const formatarData = (dataString) => {
    if (!dataString) return '';
    // Criar data com horário fixo no meio do dia para evitar problemas de fuso horário
    const [ano, mes, dia] = dataString.split('-');
    const data = new Date(ano, mes - 1, dia, 12, 0, 0);
    return data.toLocaleDateString('pt-BR');
  };

  // Função para gerar relatório específico por entidade
  const handleGerarRelatorioEntidade = (entidade) => {
    try {
      const dados = {
        transacoes: transacoesFiltradas,
        filtros
      };
      const nomeArquivo = gerarRelatorioEntidade(dados, entidade);
      toast.success(`Relatório da entidade ${entidade} gerado com sucesso: ${nomeArquivo}`);
    } catch (error) {
      console.error('Erro ao gerar relatório da entidade:', error);
      toast.error('Erro ao gerar relatório. Tente novamente.');
    }
  };

  return (
    <>
      <Header 
        onNavigateToHome={handleNavigateToHome}
        onNavigateToOrcamentos={handleNavigateToOrcamentos}
        onNavigateToDiarioObra={handleNavigateToDiarioObra}
        onLogout={handleLogout}
      />
      <div className="controle-custos-container">
        <h1 className="page-title">Controle Financeiro</h1>
        
        <div className="financeiro-resumo">
          <div className={`saldo-card ${saldoAtual >= 0 ? 'positivo' : 'negativo'}`}>
            <h3>Saldo Total</h3>
            <p className="saldo-valor">R$ {saldoAtual.toFixed(2).replace('.', ',')}</p>
          </div>
          
          <div className={`saldo-card ${saldoFiltrado >= 0 ? 'positivo' : 'negativo'}`}>
            <h3>Saldo do Período</h3>
            <p className="saldo-valor">R$ {saldoFiltrado.toFixed(2).replace('.', ',')}</p>
          </div>
        </div>
        
        {/* Resumo por Cliente/Funcionário */}
        {Object.keys(resumoPorEntidade).length > 0 && (
          <div className="entidades-resumo">
            <h2>Resumo por Entidade</h2>
            <div className="entidades-cards">
              {Object.entries(resumoPorEntidade).map(([entidadeKey, dados]) => (
                <div 
                  key={entidadeKey} 
                  className={`entidade-card ${dados.saldo >= 0 ? 'positivo' : 'negativo'}`}
                >
                  <div className="entidade-header">
                    <h3>{dados.nome}</h3>
                    <div className="entidade-actions">
                      <span className={`entidade-tipo tipo-${dados.tipo}`}>
                        {tiposEntidade[dados.tipo] || 'Outro'}
                      </span>
                      <button
                        type="button"
                        className="btn-relatorio-entidade"
                        onClick={() => handleGerarRelatorioEntidade(dados.nome)}
                        title="Gerar relatório desta entidade"
                      >
                        <i className="fas fa-file-excel"></i>
                      </button>
                    </div>
                  </div>
                  <div className="entidade-detalhes">
                    <p>Entradas: <span className="valor-entrada">R$ {dados.entradas.toFixed(2).replace('.', ',')}</span></p>
                    <p>Saídas: <span className="valor-saida">R$ {dados.saidas.toFixed(2).replace('.', ',')}</span></p>
                    <p>Saldo: <span className={dados.saldo >= 0 ? 'valor-entrada' : 'valor-saida'}>
                      R$ {dados.saldo.toFixed(2).replace('.', ',')}
                    </span></p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="controle-custos-content">
          <div className="form-section">
            <h2>{editandoId ? 'Editar Transação' : 'Nova Transação'}</h2>
            
            <form onSubmit={handleSubmit} className="transacao-form">
              <div className="form-group">
                <label>Tipo:</label>
                <div className="radio-buttons">
                  <div className={`radio-button ${formData.tipo === 'entrada' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      id="tipo-entrada"
                      name="tipo"
                      value="entrada"
                      checked={formData.tipo === 'entrada'}
                      onChange={handleChange}
                    />
                    <label htmlFor="tipo-entrada" className="entrada">Entrada</label>
                  </div>
                  
                  <div className={`radio-button ${formData.tipo === 'saida' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      id="tipo-saida"
                      name="tipo"
                      value="saida"
                      checked={formData.tipo === 'saida'}
                      onChange={handleChange}
                    />
                    <label htmlFor="tipo-saida" className="saida">Saída</label>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="descricao">Descrição:</label>
                <input
                  type="text"
                  id="descricao"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="valor">Valor (R$):</label>
                <input
                  type="text"
                  id="valor"
                  name="valor"
                  value={formData.valor}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="entidadeNome">Nome:</label>
                <input
                  type="text"
                  id="entidadeNome"
                  name="entidadeNome"
                  value={formData.entidadeNome}
                  onChange={handleChange}
                  list="entidades-list"
                  placeholder="Nome do cliente ou funcionário"
                />
                <datalist id="entidades-list">
                  {entidadesDisponiveis.map(entidade => (
                    <option key={entidade} value={entidade} />
                  ))}
                </datalist>
              </div>
              
              <div className="form-group">
                <label htmlFor="entidadeTipo">Tipo:</label>
                <select
                  id="entidadeTipo"
                  name="entidadeTipo"
                  value={formData.entidadeTipo}
                  onChange={handleChange}
                >
                  {Object.entries(tiposEntidade).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="categoria">Categoria:</label>
                <select
                  id="categoria"
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleChange}
                >
                  {Object.entries(categorias).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="data">Data:</label>
                <input
                  type="date"
                  id="data"
                  name="data"
                  value={formData.data}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="formaPagamento">Forma de Pagamento:</label>
                <select
                  id="formaPagamento"
                  name="formaPagamento"
                  value={formData.formaPagamento}
                  onChange={handleChange}
                >
                  {Object.entries(formasPagamento).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              {/* Campos de parcelamento (aparecem apenas quando categoria é pagamento_obra) */}
              {formData.categoria === 'pagamento_obra' && (
                <>
                  <div className="form-group checkbox-group">
                    <label htmlFor="isParcelamento" className="checkbox-label">
                      <input
                        type="checkbox"
                        id="isParcelamento"
                        name="isParcelamento"
                        checked={formData.isParcelamento}
                        onChange={handleChange}
                      />
                      <span>Esta é uma obra parcelada</span>
                    </label>
                  </div>
                  
                  {formData.isParcelamento && (
                    <div className="parcelamento-campos">
                      <div className="form-group">
                        <label htmlFor="valorTotal">Valor Total da Obra (R$):</label>
                        <input
                          type="text"
                          id="valorTotal"
                          name="valorTotal"
                          value={formData.valorTotal}
                          onChange={handleChange}
                          placeholder="0.00"
                          required={formData.isParcelamento}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="percentualEntrada">Percentual de Entrada (%):</label>
                        <input
                          type="number"
                          id="percentualEntrada"
                          name="percentualEntrada"
                          value={formData.percentualEntrada}
                          onChange={handleChange}
                          min="0"
                          max="100"
                          required={formData.isParcelamento}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="numeroParcelas">Número de Parcelas:</label>
                        <input
                          type="number"
                          id="numeroParcelas"
                          name="numeroParcelas"
                          value={formData.numeroParcelas}
                          onChange={handleChange}
                          min="1"
                          max="24"
                          required={formData.isParcelamento}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="intervaloParcelas">Intervalo entre Parcelas (dias):</label>
                        <input
                          type="number"
                          id="intervaloParcelas"
                          name="intervaloParcelas"
                          value={formData.intervaloParcelas}
                          onChange={handleChange}
                          min="1"
                          max="365"
                          required={formData.isParcelamento}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="dataRecebimento">Data de Recebimento da Entrada:</label>
                        <input
                          type="date"
                          id="dataRecebimento"
                          name="dataRecebimento"
                          value={formData.dataRecebimento}
                          onChange={handleChange}
                          required={formData.isParcelamento}
                        />
                        <small className="form-text">
                          As parcelas serão calculadas a partir desta data
                        </small>
                      </div>

                      <div className="form-group checkbox-group">
                        <label htmlFor="primeiraParcelaMesmoDia" className="checkbox-label">
                          <input
                            type="checkbox"
                            id="primeiraParcelaMesmoDia"
                            name="primeiraParcelaMesmoDia"
                            checked={formData.primeiraParcelaMesmoDia}
                            onChange={handleChange}
                          />
                          <span>Primeira parcela no mesmo dia da entrada</span>
                        </label>
                        <small className="form-text">
                          Se marcado, a primeira parcela será registrada na mesma data da entrada. Caso contrário, será calculada a partir da data de recebimento.
                        </small>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              <div className="form-actions">
                <button type="submit" className="btn-save">
                  {editandoId ? 'Atualizar' : 'Salvar'}
                </button>
                
                {editandoId && (
                  <button 
                    type="button" 
                    className="btn-cancel" 
                    onClick={handleCancelarEdicao}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </div>
          
          <div className="list-section">
            <h2>Histórico de Transações</h2>
            
            <div className="filtros-container">
              <div className="filtros-header">
                <h3>Filtros</h3>
                <div className="filtro-actions">
                  <div className="filtro-periodo-buttons">
                    <button 
                      type="button" 
                      className="btn-filtro-periodo"
                      onClick={() => definirFiltroPeriodo('inicio_ano')}
                    >
                      Desde Início do Ano
                    </button>
                    <button 
                      type="button" 
                      className="btn-filtro-periodo"
                      onClick={() => definirFiltroPeriodo('todo_periodo')}
                    >
                      Todo Período
                    </button>
                  </div>
                  <button 
                    type="button" 
                    className="btn-gerar-relatorio"
                    onClick={handleGerarRelatorioExcel}
                  >
                    <i className="fas fa-file-excel"></i>
                    Gerar Relatório Excel (.xlsx)
                  </button>
                  <button 
                    type="button" 
                    className="btn-gerar-relatorio"
                    onClick={handleGerarRelatorioHTML}
                  >
                    <i className="fas fa-file-code"></i>
                    Gerar Relatório Colorido (HTML)
                  </button>
                </div>
              </div>
              
              <div className="filtros-content">
                <div className="filtro-group">
                  <label htmlFor="filtroTipo">Tipo:</label>
                  <select
                    id="filtroTipo"
                    name="tipo"
                    value={filtros.tipo}
                    onChange={handleFiltroChange}
                  >
                    <option value="todos">Todos</option>
                    <option value="entrada">Entradas</option>
                    <option value="saida">Saídas</option>
                  </select>
                </div>
                
                <div className="filtro-group">
                  <label htmlFor="filtroCategoria">Categoria:</label>
                  <select
                    id="filtroCategoria"
                    name="categoria"
                    value={filtros.categoria}
                    onChange={handleFiltroChange}
                  >
                    <option value="todos">Todas</option>
                    {Object.entries(categorias).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filtro-group">
                  <label htmlFor="filtroEntidadeNome">Nome:</label>
                  <select
                    id="filtroEntidadeNome"
                    name="entidadeNome"
                    value={filtros.entidadeNome}
                    onChange={handleFiltroChange}
                  >
                    <option value="todos">Todos</option>
                    {entidadesDisponiveis.map(entidade => (
                      <option key={entidade} value={entidade}>{entidade}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filtro-group">
                  <label htmlFor="filtroEntidadeTipo">Tipo:</label>
                  <select
                    id="filtroEntidadeTipo"
                    name="entidadeTipo"
                    value={filtros.entidadeTipo}
                    onChange={handleFiltroChange}
                  >
                    <option value="todos">Todos</option>
                    {Object.entries(tiposEntidade).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filtro-group">
                  <label htmlFor="filtroDataInicio">Data Início:</label>
                  <input
                    type="date"
                    id="filtroDataInicio"
                    name="dataInicio"
                    value={filtros.dataInicio}
                    onChange={handleFiltroChange}
                  />
                </div>
                
                <div className="filtro-group">
                  <label htmlFor="filtroDataFim">Data Fim:</label>
                  <input
                    type="date"
                    id="filtroDataFim"
                    name="dataFim"
                    value={filtros.dataFim}
                    onChange={handleFiltroChange}
                  />
                </div>
              </div>
            </div>
            
            {loading ? (
              <p className="loading-text">Carregando transações...</p>
            ) : transacoesFiltradas.length > 0 ? (
              <div className="transacoes-list">
                {transacoesFiltradas.map((transacao) => (
                  <div 
                    key={transacao.id} 
                    className={`transacao-item ${transacao.tipo === 'entrada' ? 'entrada' : 'saida'}`}
                  >
                    <div className="transacao-header">
                      <div className="transacao-tipo-valor">
                        <span className={`transacao-tipo ${transacao.tipo}`}>
                          {transacao.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                        </span>
                        <div className="transacao-data">
                        <span className="transacao-id">ID: {transacao.id ? transacao.id.replace('transacao_', '') : 'N/A'}</span>
                        </div>

                      </div>
                      <div className="transacao-data">
                        {formatarData(transacao.data)}
                        <br />
                      </div>
                    </div>
                    
                    <div className="transacao-descricao">
                      {transacao.descricao}
                    </div>
                    
                    <div className="transacao-valor-destaque">
                      <strong>Valor:</strong> R$ {parseFloat(transacao.valor).toFixed(2).replace('.', ',')}
                    </div>
                    
                    <div className="transacao-detalhes">
                      <span className="transacao-categoria">
                        {categorias[transacao.categoria] || 'Geral'}
                      </span>
                      {transacao.formaPagamento && (
                        <span className="transacao-pagamento">
                          {formasPagamento[transacao.formaPagamento] || transacao.formaPagamento}
                        </span>
                      )}
                      {transacao.tipo === 'saida' && transacao.reembolsadoPeloCliente && (
                        <span className="transacao-reembolsado">Reembolsado pelo cliente</span>
                      )}
                    </div>
                    
                    {/* Exibir cliente/funcionário se existir */}
                    {transacao.entidadeNome && (
                      <div className="transacao-entidade">
                        {transacao.entidadeNome} - {tiposEntidade[transacao.entidadeTipo] || 'Outro'}
                      </div>
                    )}
                    
                    {/* Exibir reembolsado pelo cliente de forma destacada */}
                    {transacao.tipo === 'saida' && transacao.reembolsadoPeloCliente && (
                      <div className="transacao-reembolsado-destaque">
                        <i className="fa fa-check-circle"></i> Reembolsado pelo cliente
                      </div>
                    )}
                    
                    {/* Adicionar informações de parcelamento se for uma obra parcelada */}
                    {transacao.categoria === 'pagamento_obra' && transacao.parcelaInfo && (
                      <div className="parcela-info">
                        {transacao.parcelaInfo.numero === 0 ? (
                          <span className="parcela-entrada">Entrada</span>
                        ) : (
                          <span className="parcela-numero">
                            Parcela {transacao.parcelaInfo.numero}/{transacao.parcelaInfo.total - 1}
                          </span>
                        )}
                        <span className="parcela-valor-total">
                          Valor Total: R$ {parseFloat(transacao.parcelaInfo.valorTotal).toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                    )}
                    
                    <div className="transacao-acoes">
                      <button 
                        onClick={() => handleEditar(transacao)}
                        className="btn-edit"
                      >
                        Editar
                      </button>
                      
                      {transacao.tipo === 'saida' && !transacao.reembolsadoPeloCliente && (
                        <button 
                          onClick={() => handleMarcarReembolso(transacao)}
                          className="btn-reembolso"
                        >
                          Marcar reembolso
                        </button>
                      )}
                      
                      <button 
                        onClick={() => handleExcluir(transacao.id)}
                        className="btn-delete"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-list">Nenhuma transação encontrada para os filtros selecionados.</p>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Edição */}
      {modalAberta && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Editar Transação</h2>
            
            <form onSubmit={handleModalSubmit} className="transacao-form">
              <div className="form-group">
                <label>Tipo:</label>
                <div className="radio-buttons">
                  <div className={`radio-button ${modalData.tipo === 'entrada' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      id="modal-tipo-entrada"
                      name="tipo"
                      value="entrada"
                      checked={modalData.tipo === 'entrada'}
                      onChange={handleModalChange}
                    />
                    <label htmlFor="modal-tipo-entrada" className="entrada">Entrada</label>
                  </div>
                  
                  <div className={`radio-button ${modalData.tipo === 'saida' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      id="modal-tipo-saida"
                      name="tipo"
                      value="saida"
                      checked={modalData.tipo === 'saida'}
                      onChange={handleModalChange}
                    />
                    <label htmlFor="modal-tipo-saida" className="saida">Saída</label>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-descricao">Descrição:</label>
                <input
                  type="text"
                  id="modal-descricao"
                  name="descricao"
                  value={modalData.descricao}
                  onChange={handleModalChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-valor">Valor (R$):</label>
                <input
                  type="text"
                  id="modal-valor"
                  name="valor"
                  value={modalData.valor}
                  onChange={handleModalChange}
                  placeholder="0.00"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-entidadeNome">Nome:</label>
                <input
                  type="text"
                  id="modal-entidadeNome"
                  name="entidadeNome"
                  value={modalData.entidadeNome}
                  onChange={handleModalChange}
                  list="entidades-list"
                  placeholder="Nome do cliente ou funcionário"
                />
                <datalist id="entidades-list">
                  {entidadesDisponiveis.map(entidade => (
                    <option key={entidade} value={entidade} />
                  ))}
                </datalist>
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-entidadeTipo">Tipo:</label>
                <select
                  id="modal-entidadeTipo"
                  name="entidadeTipo"
                  value={modalData.entidadeTipo}
                  onChange={handleModalChange}
                >
                  {Object.entries(tiposEntidade).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-categoria">Categoria:</label>
                <select
                  id="modal-categoria"
                  name="categoria"
                  value={modalData.categoria}
                  onChange={handleModalChange}
                >
                  {Object.entries(categorias).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-data">Data:</label>
                <input
                  type="date"
                  id="modal-data"
                  name="data"
                  value={modalData.data}
                  onChange={handleModalChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-formaPagamento">Forma de Pagamento:</label>
                <select
                  id="modal-formaPagamento"
                  name="formaPagamento"
                  value={modalData.formaPagamento}
                  onChange={handleModalChange}
                >
                  {Object.entries(formasPagamento).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              {modalData.tipo === 'saida' && (
                <div className="form-group checkbox-group">
                  <label htmlFor="modal-reembolsadoPeloCliente" className="checkbox-label">
                    <input
                      type="checkbox"
                      id="modal-reembolsadoPeloCliente"
                      name="reembolsadoPeloCliente"
                      checked={modalData.reembolsadoPeloCliente}
                      onChange={handleModalChange}
                    />
                    <span>Reembolsado pelo cliente</span>
                  </label>
                  {!transacoes.find(t => t.id === editandoId)?.reembolsadoPeloCliente && modalData.reembolsadoPeloCliente && (
                    <p className="reembolso-info">
                      Será criada uma transação de entrada com o mesmo valor.
                    </p>
                  )}
                </div>
              )}
              
              <div className="modal-actions">
                <button type="submit" className="btn-save">
                  Salvar
                </button>
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={handleCancelarEdicao}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal de Confirmação de Reembolso */}
      {confirmacaoReembolso && (
        <div className="modal-overlay">
          <div className="modal-content confirmacao-modal">
            <h2>Confirmar Reembolso</h2>
            
            <div className="confirmacao-content">
              <div className="confirmacao-icon">
                <i className="fa fa-money-bill"></i>
              </div>
              
              <p>
                Deseja marcar esta transação como <strong>reembolsada pelo cliente</strong>?
              </p>
              
              <div className="confirmacao-details">
                <p><strong>Descrição:</strong> {transacaoParaReembolso?.descricao}</p>
                <p><strong>Valor:</strong> R$ {parseFloat(transacaoParaReembolso?.valor || 0).toFixed(2).replace('.', ',')}</p>
              </div>
              
              <div className="confirmacao-info">
                <i className="fa fa-info-circle"></i>
                <span>Uma nova transação de entrada será criada automaticamente com o mesmo valor.</span>
              </div>
            </div>
            
            <div className="confirmacao-actions">
              <button 
                onClick={confirmarReembolso}
                className="btn-confirm"
              >
                Confirmar
              </button>
              <button 
                onClick={cancelarReembolso}
                className="btn-cancel"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ControleCustos; 